import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArchiveIcon,
  BotIcon,
  CalendarPlusIcon,
  CheckCircle2Icon,
  ChevronLeftIcon,
  ClockIcon,
  Loader2Icon,
  MoreVerticalIcon,
  PanelRightIcon,
  RepeatIcon,
  UserCheckIcon,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { AttendanceDetailsPanel } from '@/features/inbox/attendance-details-panel'
import { ChatComposer } from '@/features/inbox/chat-composer'
import { ChatSurface } from '@/features/inbox/chat-ui/chat-surface'
import { DateSeparator } from '@/features/inbox/chat-ui/date-separator'
import { MessageBubble } from '@/features/inbox/message-bubble'
import {
  useInboxAgents,
  useInboxDepartments,
  useInboxMembers,
} from '@/hooks/use-attendance-options'
import { useAuth } from '@/hooks/use-auth'
import {
  useContactCadences,
  usePauseContactCadences,
} from '@/hooks/use-contacts'
import {
  useArchiveAttendance,
  useAssumeAttendance,
  useFinalizationReasons,
  useFinalizeConversation,
  useTransferAttendance,
  useUpdateConversation,
} from '@/hooks/use-conversation-actions'
import { useConversation, useMessages } from '@/hooks/use-messages'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { useOutboxMessages } from '@/hooks/use-outbox'
import { formatDayLabel, getInitials } from '@/lib/format'
import { prefetchInboxMedia, recentMediaUrls } from '@/lib/media-cache'
import {
  getWhatsAppWindowStatus,
  useWhatsAppWindowCountdown,
} from '@/lib/whatsapp-window'
import type { MessageRow } from '@/types/inbox'

export const Route = createFileRoute('/_app/conversas/$conversationId')({
  component: ChatPage,
})

// URL do painel web (opcional) — habilita atalhos que só existem lá, como
// criar agendamento.
const WEB_APP_URL = (
  import.meta.env.VITE_WEB_APP_URL as string | undefined
)?.replace(/\/$/, '')

function groupByDay(messages: MessageRow[]) {
  const groups: Array<{ day: string; items: MessageRow[] }> = []
  for (const message of messages) {
    const day = new Date(message.createdAt).toDateString()
    const last = groups[groups.length - 1]
    if (last && last.day === day) {
      last.items.push(message)
    } else {
      groups.push({ day, items: [message] })
    }
  }
  return groups
}

function ChatPage() {
  const { conversationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentOrgId, user, currentRole, usingCachedSession } = useAuth()
  const online = useOnlineStatus()

  const { data: conversation } = useConversation(currentOrgId, conversationId)
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMessages(currentOrgId, conversationId)

  const update = useUpdateConversation(currentOrgId, conversationId)
  const assume = useAssumeAttendance(currentOrgId, conversationId)
  const transfer = useTransferAttendance(currentOrgId, conversationId)
  const finalize = useFinalizeConversation(currentOrgId, conversationId)
  const archive = useArchiveAttendance(currentOrgId, conversationId)
  const { data: finalizationReasons = [], isLoading: reasonsLoading } =
    useFinalizationReasons(currentOrgId)
  const { data: members = [] } = useInboxMembers(currentOrgId)
  const { data: departments = [] } = useInboxDepartments(currentOrgId)
  const { data: agents = [] } = useInboxAgents(currentOrgId)
  const [finalizationOpen, setFinalizationOpen] = useState(false)
  const [finalizationReasonId, setFinalizationReasonId] = useState('')
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferDepartmentId, setTransferDepartmentId] = useState('')
  const [transferUserId, setTransferUserId] = useState('none')
  const [aiOpen, setAiOpen] = useState(false)
  const [selectedAgentId, setSelectedAgentId] = useState('channel-default')
  const [detailsOpen, setDetailsOpen] = useState(false)

  const outboxMessages = useOutboxMessages(conversationId)

  const messages = useMemo(() => {
    const server = data?.pages.flatMap((page) => page.data) ?? []
    // Drop optimistic outbox entries the server has already confirmed.
    const confirmed = new Set(
      server.map((m) => m.clientMessageId).filter(Boolean)
    )
    const pending = outboxMessages.filter(
      (m) => !confirmed.has(m.clientMessageId)
    )
    const sortedServer = [...server].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
    const sortedPending = [...pending].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
    return [...sortedServer, ...sortedPending]
  }, [data, outboxMessages])

  const groups = useMemo(() => groupByDay(messages), [messages])
  const mediaUrlsToPrefetch = useMemo(
    () => recentMediaUrls(messages),
    [messages]
  )

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const lastMessageId = messages[messages.length - 1]?.id

  useEffect(() => {
    if (!online || usingCachedSession) return
    void queryClient.invalidateQueries({
      queryKey: ['conversations', currentOrgId],
    })
    void queryClient.invalidateQueries({
      queryKey: ['conversation', currentOrgId, conversationId],
    })
    void queryClient.invalidateQueries({
      queryKey: ['messages', currentOrgId, conversationId],
    })
  }, [conversationId, currentOrgId, online, queryClient, usingCachedSession])

  useEffect(() => {
    if (!online || mediaUrlsToPrefetch.length === 0) return
    void prefetchInboxMedia(mediaUrlsToPrefetch)
  }, [mediaUrlsToPrefetch, online])

  function scrollToBottom(behavior: ScrollBehavior = 'auto') {
    const node = scrollRef.current
    if (!node) return
    node.scrollTo({ top: node.scrollHeight, behavior })
  }

  // Stick to bottom when new messages arrive.
  useEffect(() => {
    if (!lastMessageId) return
    scrollToBottom('smooth')
  }, [lastMessageId])

  // Cold opens from push notifications render after the service worker focuses
  // or opens the route, so anchor the loaded conversation after layout settles.
  useEffect(() => {
    if (isLoading || !lastMessageId) return
    scrollToBottom()
    let secondFrame: number | undefined
    const firstFrame = window.requestAnimationFrame(() => {
      scrollToBottom()
      secondFrame = window.requestAnimationFrame(() => scrollToBottom())
    })
    return () => {
      window.cancelAnimationFrame(firstFrame)
      if (secondFrame) window.cancelAnimationFrame(secondFrame)
    }
  }, [conversationId, isLoading, lastMessageId])

  // Load older messages when scrolled near the top.
  const topSentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = topSentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver((entries) => {
      if (
        online &&
        entries[0]?.isIntersecting &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        void fetchNextPage()
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, online])

  const name =
    conversation?.customer?.name ?? conversation?.customer?.phone ?? 'Conversa'
  const isFinalized = conversation?.status === 'FINALIZED'
  const windowStatus = getWhatsAppWindowStatus(conversation)
  const windowTimeLeft = useWhatsAppWindowCountdown(
    windowStatus === 'active' || windowStatus === 'expiring'
      ? conversation?.lastInboundAt
      : null
  )

  const { data: cadences = [] } = useContactCadences(
    currentOrgId,
    conversation?.leadId
  )
  const pauseCadences = usePauseContactCadences(
    currentOrgId,
    conversation?.leadId
  )
  const activeCadences = cadences.filter((c) => c.status === 'ACTIVE')

  const currentUserId = user?.id ?? null
  const isManagerOrAbove =
    currentRole === 'OWNER' ||
    currentRole === 'ADMIN' ||
    currentRole === 'MANAGER'
  const isOpen = conversation?.status === 'OPEN'
  const assumedByMe =
    !!currentUserId && conversation?.assignedTo === currentUserId
  const canAssume =
    isOpen &&
    (conversation?.mode === 'WAITING_HUMAN' ||
      conversation?.mode === 'AI_ACTIVE') &&
    conversation?.assignedTo !== currentUserId
  // Lead em cadência ativa ou IA conduzindo e conversa não assumida →
  // bloqueia o envio para evitar mensagens concorrentes.
  const cadenceBlocked = activeCadences.length > 0 && !assumedByMe
  const aiBlocked = isOpen && conversation?.mode === 'AI_ACTIVE' && !assumedByMe
  const optedOut = conversation?.customer?.optInStatus === 'OPTED_OUT'
  const cannotSend = conversation
    ? !isOpen ||
      windowStatus === 'expired' ||
      optedOut ||
      (canAssume && !isManagerOrAbove) ||
      cadenceBlocked ||
      aiBlocked
    : false
  const showTemplates = windowStatus === 'expired' && isOpen && !optedOut
  const sendDisabledReason = showTemplates
    ? 'Janela de 24h expirada. Template não reabre a janela — só a resposta do lead reabre.'
    : undefined
  const inputPlaceholder = optedOut
    ? 'Contato optou por não receber mensagens'
    : windowStatus === 'expired'
      ? 'Janela expirada — apenas templates'
      : cadenceBlocked
        ? 'Lead em cadência — assuma ou pause para enviar'
        : aiBlocked
          ? 'IA conduzindo — assuma para enviar'
          : !isOpen
            ? 'Atendimento não está aberto'
            : 'Assuma o atendimento para enviar'

  const membersById = useMemo(
    () =>
      new Map(
        members.map((member) => [member.id, member.name ?? member.email])
      ),
    [members]
  )
  function resolveAuthorName(message: MessageRow): string | null {
    if (message.authorType === 'AI') {
      return conversation?.aiAgent?.name ?? 'IA'
    }
    if (message.authorType === 'HUMAN') {
      return message.authorId
        ? (membersById.get(message.authorId) ?? null)
        : null
    }
    if (
      message.authorType === 'CADENCE' ||
      message.authorType === 'FOLLOWUP' ||
      message.authorType === 'AUTOMATION'
    ) {
      return 'Automação'
    }
    return null
  }

  const handleToggleAi = () =>
    update.mutate({ aiEnabled: !conversation?.aiEnabled })

  const transferEligibleMembers = useMemo(
    () =>
      transferDepartmentId
        ? members.filter((member) =>
            member.departmentIds.includes(transferDepartmentId)
          )
        : members,
    [members, transferDepartmentId]
  )

  function openTransferDialog() {
    setTransferDepartmentId(conversation?.departmentId ?? '')
    setTransferUserId(conversation?.assignedTo ?? 'none')
    setTransferOpen(true)
  }

  function confirmTransfer() {
    if (!transferDepartmentId) return
    transfer.mutate(
      {
        departmentId: transferDepartmentId,
        assignedUserId: transferUserId === 'none' ? null : transferUserId,
      },
      {
        onSuccess: () => setTransferOpen(false),
      }
    )
  }

  function openAiDialog() {
    setSelectedAgentId(conversation?.aiAgentId ?? 'channel-default')
    setAiOpen(true)
  }

  function activateAi() {
    update.mutate(
      {
        aiEnabled: true,
        aiAgentId:
          selectedAgentId === 'channel-default' ? null : selectedAgentId,
      },
      {
        onSuccess: () => setAiOpen(false),
      }
    )
  }

  function pauseAi() {
    update.mutate(
      { aiEnabled: false },
      {
        onSuccess: () => setAiOpen(false),
      }
    )
  }

  function openFinalizationDialog() {
    setFinalizationReasonId('')
    setFinalizationOpen(true)
  }

  function confirmFinalization() {
    if (finalizationReasons.length > 0 && !finalizationReasonId) return
    finalize.mutate(
      { reasonId: finalizationReasonId || undefined },
      {
        onSuccess: () => {
          setFinalizationOpen(false)
          setFinalizationReasonId('')
        },
      }
    )
  }

  const renderDetailsPanel = (onConversationNavigate?: () => void) => (
    <AttendanceDetailsPanel
      conversation={conversation}
      windowStatus={windowStatus}
      windowTimeLeft={windowTimeLeft}
      departments={departments}
      onAssume={() => assume.mutate()}
      onTransfer={openTransferDialog}
      onConfigureAi={openAiDialog}
      onFinalize={openFinalizationDialog}
      onArchive={() => archive.mutate()}
      onToggleAi={handleToggleAi}
      onConversationNavigate={onConversationNavigate}
      canAssume={canAssume}
      assumePending={assume.isPending}
      transferPending={transfer.isPending}
      finalizePending={finalize.isPending}
      archivePending={archive.isPending}
      updatePending={update.isPending}
    />
  )

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <section className="flex min-w-0 flex-1 flex-col">
        <header className="border-border bg-card flex h-14 shrink-0 items-center gap-2 border-b px-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 lg:hidden"
            // Volta para a lista com os mesmos filtros.
            onClick={() => navigate({ to: '/conversas', search: true })}
            aria-label="Voltar"
          >
            <ChevronLeftIcon className="size-5" />
          </Button>
          <Avatar className="size-9 shrink-0">
            {conversation?.customer?.avatarUrl ? (
              <AvatarImage src={conversation.customer.avatarUrl} alt="" />
            ) : null}
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{name}</p>
            {conversation?.aiEnabled ? (
              <p className="text-muted-foreground flex items-center gap-1 text-xs">
                <BotIcon className="size-3" /> IA ativa
              </p>
            ) : conversation?.assignedMember ? (
              <p className="text-muted-foreground truncate text-xs">
                {conversation.assignedMember.name ?? 'Atribuído'}
              </p>
            ) : null}
          </div>
          {activeCadences.length > 0 ? (
            <Badge
              variant="outline"
              className="hidden shrink-0 gap-1 border-purple-300 text-purple-600 md:inline-flex dark:border-purple-800 dark:text-purple-400"
            >
              <RepeatIcon className="size-3" />
              {activeCadences.length === 1
                ? activeCadences[0]!.cadence.name
                : `${activeCadences.length} cadências`}
            </Badge>
          ) : null}
          {(windowStatus === 'active' || windowStatus === 'expiring') &&
          windowTimeLeft ? (
            <Badge
              variant="outline"
              className={cn(
                'hidden shrink-0 gap-1 md:inline-flex',
                windowStatus === 'expiring'
                  ? 'border-orange-300 text-orange-700 dark:border-orange-800 dark:text-orange-400'
                  : 'border-emerald-300 text-emerald-600 dark:border-emerald-800 dark:text-emerald-400'
              )}
            >
              <ClockIcon className="size-3" />
              {windowStatus === 'expiring' ? 'Expira: ' : ''}
              {windowTimeLeft}
            </Badge>
          ) : null}
          {!isFinalized ? (
            <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
              {canAssume ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={assume.isPending}
                  onClick={() => assume.mutate()}
                >
                  Assumir
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={transfer.isPending}
                onClick={openTransferDialog}
              >
                Transferir
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                disabled={update.isPending}
                onClick={openAiDialog}
              >
                <BotIcon className="size-4" />
                {conversation?.aiEnabled
                  ? (conversation.aiAgent?.name ?? 'IA ativa')
                  : 'IA'}
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={finalize.isPending}
                onClick={openFinalizationDialog}
              >
                Finalizar
              </Button>
            </div>
          ) : null}
          <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 shrink-0 xl:hidden"
                aria-label="Detalhes do atendimento"
              >
                <PanelRightIcon className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[92vw] gap-0 p-0 sm:max-w-md">
              <SheetHeader className="sr-only">
                <SheetTitle>Detalhes do atendimento</SheetTitle>
                <SheetDescription>
                  Dados operacionais da conversa aberta.
                </SheetDescription>
              </SheetHeader>
              {renderDetailsPanel(() => setDetailsOpen(false))}
            </SheetContent>
          </Sheet>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 shrink-0"
                aria-label="Ações"
              >
                <MoreVerticalIcon className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {canAssume ? (
                <DropdownMenuItem
                  disabled={assume.isPending}
                  onClick={() => assume.mutate()}
                >
                  <UserCheckIcon className="size-4" />
                  Assumir atendimento
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={openAiDialog}>
                <BotIcon className="size-4" />
                {conversation?.aiEnabled ? 'Configurar IA' : 'Ativar IA'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openTransferDialog}>
                <UserCheckIcon className="size-4" />
                Transferir atendimento
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={isFinalized}
                onClick={openFinalizationDialog}
              >
                <CheckCircle2Icon className="size-4" />
                Finalizar atendimento
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={archive.isPending}
                onClick={() => archive.mutate()}
              >
                <ArchiveIcon className="size-4" />
                Arquivar atendimento
              </DropdownMenuItem>
              {WEB_APP_URL ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      window.open(
                        `${WEB_APP_URL}/agendamentos?new`,
                        '_blank',
                        'noopener'
                      )
                    }
                  >
                    <CalendarPlusIcon className="size-4" />
                    Criar agendamento
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Cadência ativa — envio bloqueado até assumir ou pausar */}
        {cadenceBlocked ? (
          <div className="border-border shrink-0 space-y-2 border-b bg-purple-50 px-4 py-2 text-xs text-purple-800 dark:bg-purple-900/10 dark:text-purple-300">
            <div className="flex items-center gap-1.5">
              <RepeatIcon className="size-3.5 shrink-0" />
              Lead em cadência ativa. Assuma a conversa ou pause a cadência para
              enviar mensagens.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={assume.isPending}
                onClick={() => assume.mutate()}
              >
                Assumir conversa
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={pauseCadences.isPending}
                onClick={() =>
                  pauseCadences.mutate(activeCadences.map((c) => c.id))
                }
              >
                Pausar cadência
              </Button>
            </div>
          </div>
        ) : null}

        {/* IA conduzindo — envio bloqueado até assumir */}
        {aiBlocked && !cadenceBlocked ? (
          <div className="border-border shrink-0 space-y-2 border-b bg-violet-50 px-4 py-2 text-xs text-violet-800 dark:bg-violet-900/10 dark:text-violet-300">
            <div className="flex items-center gap-1.5">
              <BotIcon className="size-3.5 shrink-0" />A IA está conduzindo este
              atendimento. Assuma a conversa para enviar mensagens.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={assume.isPending}
                onClick={() => assume.mutate()}
              >
                Assumir conversa
              </Button>
            </div>
          </div>
        ) : null}

        <ChatSurface ref={scrollRef}>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2Icon className="text-muted-foreground size-6 animate-spin" />
            </div>
          ) : (
            <>
              <div ref={topSentinelRef} className="h-1" />
              {isFetchingNextPage ? (
                <div className="flex justify-center py-2">
                  <Loader2Icon className="text-muted-foreground size-4 animate-spin" />
                </div>
              ) : null}
              {groups.map((group) => (
                <div key={group.day} className="space-y-1">
                  <DateSeparator
                    label={formatDayLabel(group.items[0]!.createdAt)}
                  />
                  {group.items.map((message) => (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      authorName={resolveAuthorName(message)}
                    />
                  ))}
                </div>
              ))}
              <div ref={bottomRef} />
            </>
          )}
        </ChatSurface>

        {isFinalized ? (
          <div className="border-border bg-card text-muted-foreground shrink-0 border-t px-4 py-4 text-center text-sm">
            Atendimento finalizado
          </div>
        ) : (
          <ChatComposer
            orgId={currentOrgId}
            conversationId={conversationId}
            channelId={conversation?.channelId}
            disabled={cannotSend}
            disabledReason={sendDisabledReason}
            placeholder={inputPlaceholder}
            windowTimeLeft={windowTimeLeft}
          />
        )}
      </section>

      <div className="border-border hidden w-[300px] shrink-0 border-l xl:flex 2xl:w-[320px]">
        {renderDetailsPanel()}
      </div>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Transferir atendimento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-muted-foreground text-xs font-medium">
                Setor
              </label>
              <Select
                value={transferDepartmentId || undefined}
                onValueChange={(value) => {
                  setTransferDepartmentId(value)
                  const stillEligible = members.some(
                    (member) =>
                      member.id === transferUserId &&
                      member.departmentIds.includes(value)
                  )
                  if (!stillEligible) setTransferUserId('none')
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um setor" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={department.id}>
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-muted-foreground text-xs font-medium">
                Responsavel
              </label>
              <Select value={transferUserId} onValueChange={setTransferUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um responsavel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem responsavel</SelectItem>
                  {transferEligibleMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name ?? member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTransferOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!transferDepartmentId || transfer.isPending}
              onClick={confirmTransfer}
            >
              {transfer.isPending ? 'Transferindo...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>IA do atendimento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-muted-foreground text-xs font-medium">
                Agente
              </label>
              <Select
                value={selectedAgentId}
                onValueChange={setSelectedAgentId}
                disabled={agents.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma IA" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="channel-default">
                    IA padrao do canal
                  </SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {agents.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nenhum agente configurado para esta organizacao.
              </p>
            ) : null}
          </div>
          <DialogFooter>
            {conversation?.aiEnabled ? (
              <Button
                type="button"
                variant="outline"
                disabled={update.isPending}
                onClick={pauseAi}
              >
                Pausar IA
              </Button>
            ) : null}
            <Button
              type="button"
              disabled={update.isPending || agents.length === 0}
              onClick={activateAi}
            >
              {conversation?.aiEnabled ? 'Aplicar' : 'Ativar IA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={finalizationOpen} onOpenChange={setFinalizationOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Finalizar atendimento</DialogTitle>
            <DialogDescription>
              Selecione o motivo para finalizar este atendimento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="finalization-reason">Motivo</Label>
              {reasonsLoading ? (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Loader2Icon className="size-4 animate-spin" />
                  Carregando motivos...
                </div>
              ) : finalizationReasons.length > 0 ? (
                <Select
                  value={finalizationReasonId || undefined}
                  onValueChange={setFinalizationReasonId}
                >
                  <SelectTrigger id="finalization-reason">
                    <SelectValue placeholder="Selecione um motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {finalizationReasons.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Nenhum motivo configurado. O atendimento sera finalizado sem
                  motivo.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={finalize.isPending}
              onClick={() => setFinalizationOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={
                finalize.isPending ||
                reasonsLoading ||
                (finalizationReasons.length > 0 && !finalizationReasonId)
              }
              onClick={confirmFinalization}
            >
              {finalize.isPending ? 'Finalizando...' : 'Finalizar atendimento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
